import { useEffect, useMemo, useState, type DragEvent } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, CalendarClock, CalendarDays, ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import { addDays, type LocalDate } from '@habitos/engine';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import { buildAgenda, SLOTS, type AgendaEntry, type Conflict, type Slot } from '../domain/agenda';
import { rescheduleTask, setTaskDone } from '../domain/actions';
import { toEngineSettings, type HabitRec } from '../domain/model';
import { getMeta, setMeta } from '../db/db';
import { SLOT_DEFAULT } from '../assistant/normalize';
import { Field, Segmented } from '../ui/Field';
import { Sheet } from '../ui/Sheet';
import { useToast } from '../ui/Toast';
import { Empty } from '../ui/Empty';
import { HabitCard } from './HabitCard';
import { WeekView } from './WeekView';
import { DayTimeline } from './DayTimeline';

/** Tipo MIME propio para arrastrar tareas entre franjas y días (solo con ratón o trackpad). */
export const DRAG_TYPE = 'application/x-habitos-task';

/** Hora al soltar una tarea en una franja: la misma que propone el chat; "sin hora" la quita. */
export const timeForSlot = (slot: Slot): string | null => (slot === 'anytime' ? null : SLOT_DEFAULT[slot]);

/** Nombres de las actividades con las que choca cada una. */
export function conflictNames(conflicts: Conflict[], entries: AgendaEntry[]): Map<string, string[]> {
  const name = new Map(entries.map((e) => [e.key, e.title]));
  const out = new Map<string, string[]>();
  for (const { a, b } of conflicts) {
    out.set(a, [...(out.get(a) ?? []), name.get(b) ?? '']);
    out.set(b, [...(out.get(b) ?? []), name.get(a) ?? '']);
  }
  return out;
}

export function useMoveTask() {
  const { t } = useI18n();
  const toast = useToast();
  return async (task: HabitRec, date: LocalDate, time: string | null) => {
    const prev = await rescheduleTask(task, date, time);
    toast.show(t('agenda.moved'), { label: t('common.undo'), run: () => void rescheduleTask(task, prev.dueDate, prev.dueTime) });
  };
}

/** Día por franjas, día por horas (línea de tiempo) o semana: un solo selector. */
type Scope = 'day' | 'hours' | 'week';

/** Agenda: un día por franjas o por horas, o la semana completa. Ambas leen los mismos datos (hábitos, tareas, recordatorios). */
export function AgendaView() {
  const { t } = useI18n();
  const { ctx } = useApp();
  const [scope, setScope] = useState<Scope>('day');
  const [day, setDay] = useState<LocalDate>(ctx.today);
  const [moving, setMoving] = useState<HabitRec | null>(null);

  useEffect(() => {
    void getMeta<Scope>('agendaScope', 'day').then((v) => setScope(v === 'hours' || v === 'week' ? v : 'day'));
  }, []);

  return (
    <div className="stack">
      <Segmented
        legend={t('agenda.scope')}
        value={scope}
        onChange={(v) => {
          setScope(v);
          void setMeta('agendaScope', v);
        }}
        options={[
          { value: 'day', label: t('agenda.scopes.day') },
          { value: 'hours', label: t('agenda.dayModes.hours') },
          { value: 'week', label: t('agenda.scopes.week') },
        ]}
      />
      {scope !== 'week' ? (
        <DayView day={day} setDay={setDay} onMove={setMoving} mode={scope === 'hours' ? 'hours' : 'slots'} />
      ) : (
        <WeekView
          anchor={day}
          setAnchor={setDay}
          onMove={setMoving}
          onOpenDay={(d) => {
            setDay(d);
            setScope('day');
            void setMeta('agendaScope', 'day');
          }}
        />
      )}
      <RescheduleSheet task={moving} today={ctx.today} onClose={() => setMoving(null)} />
    </div>
  );
}

function DayView({ day, setDay, onMove, mode }: { day: LocalDate; setDay: (d: LocalDate) => void; onMove: (h: HabitRec) => void; mode: 'slots' | 'hours' }) {
  const { t, fmtDate } = useI18n();
  const { data, ctx, idx } = useApp();
  const move = useMoveTask();
  const [over, setOver] = useState<Slot | null>(null);
  const [dragging, setDragging] = useState(false);
  const settings = useMemo(() => toEngineSettings(ctx.settings), [ctx.settings]);
  const agenda = useMemo(
    () => buildAgenda({ habits: data.habits, reminders: data.reminders, notes: data.notes, vacations: data.vacations, idx, settings, day, today: ctx.today }),
    [data.habits, data.reminders, data.notes, data.vacations, idx, settings, day, ctx.today],
  );
  const isToday = day === ctx.today;
  const all = SLOTS.flatMap((s) => agenda.slots[s]);
  const empty = all.length === 0;
  const clashes = conflictNames(agenda.conflicts, all);

  const drop = (slot: Slot) => (e: DragEvent) => {
    e.preventDefault();
    setOver(null);
    setDragging(false);
    const task = data.habits.find((h) => h.id === e.dataTransfer.getData(DRAG_TYPE));
    if (task && day >= ctx.today) void move(task, day, timeForSlot(slot));
  };

  return (
    <>
      <nav className="row" aria-label={t('agenda.dayNav')} style={{ justifyContent: 'space-between' }}>
        <button type="button" className="icon-btn" aria-label={t('agenda.prevDay')} onClick={() => setDay(addDays(day, -1))}>
          <ChevronLeft size={22} aria-hidden="true" />
        </button>
        <div style={{ textAlign: 'center' }}>
          <strong aria-live="polite">{isToday ? t('agenda.today') : fmtDate(day, { weekday: 'long', day: 'numeric', month: 'long' })}</strong>
          <div className="muted xsmall">{t('agenda.summary', { pending: agenda.pending, done: agenda.done })}</div>
        </div>
        <button type="button" className="icon-btn" aria-label={t('agenda.nextDay')} onClick={() => setDay(addDays(day, 1))}>
          <ChevronRight size={22} aria-hidden="true" />
        </button>
      </nav>
      {!isToday && (
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDay(ctx.today)}>
          {t('agenda.backToToday')}
        </button>
      )}
      {agenda.conflicts.length > 0 && (
        <p className="banner banner--warning small" role="note">
          <AlertTriangle size={18} aria-hidden="true" /> {t('agenda.conflictsSummary', { count: agenda.conflicts.length })}
        </p>
      )}

      {!empty && mode === 'hours' ? (
        <DayTimeline entries={all} day={day} today={ctx.today} tz={ctx.tz} onMove={onMove} />
      ) : empty ? (
        <Empty title={t('agenda.emptyTitle')} body={isToday ? t('agenda.emptyBodyToday') : t('agenda.emptyBodyOther')} />
      ) : (
        <div
          className="stack"
          onDragStart={() => setDragging(true)}
          onDragEnd={() => {
            setDragging(false);
            setOver(null);
          }}
        >
          {SLOTS.map((slot) => {
            const items = agenda.slots[slot];
            // Las franjas vacías solo aparecen como destino mientras se arrastra.
            if (!items.length && !dragging) return null;
            return (
              <section
                key={slot}
                className={`stack agenda__slot${over === slot ? ' agenda__slot--over' : ''}`}
                aria-labelledby={`slot-${slot}`}
                onDragOver={(e) => {
                  if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
                  e.preventDefault();
                  setOver(slot);
                }}
                onDragLeave={() => setOver((o) => (o === slot ? null : o))}
                onDrop={drop(slot)}
              >
                <h2 id={`slot-${slot}`} className="section-title">
                  {t(`agenda.slots.${slot}`)}
                </h2>
                {items.map((e) => (
                  <AgendaRow key={e.key} entry={e} isToday={isToday} onMove={onMove} clashes={clashes.get(e.key)} />
                ))}
                {!items.length && <p className="muted small agenda__drop-hint">{t('agenda.dropHere')}</p>}
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}

/** Hora de inicio y fin; la duración estimada se marca para no presentarla como un dato. */
export function TimeRange({ entry, compact = false }: { entry: AgendaEntry; compact?: boolean }) {
  const { t } = useI18n();
  if (!entry.time) return null;
  return (
    <span className={`num muted${compact ? ' week__time' : ''}`}>
      {entry.time}
      {entry.end ? `–${entry.end}` : ''}
      {entry.estimated && <span className="visually-hidden"> ({t('agenda.estimated')})</span>}
      {entry.estimated ? '*' : ''}
      {compact ? '' : ' · '}
    </span>
  );
}

function Clash({ names }: { names?: string[] }) {
  const { t } = useI18n();
  if (!names?.length) return null;
  return (
    <span className="badge badge--warning">
      <AlertTriangle size={12} aria-hidden="true" /> {t('agenda.overlaps', { names: names.join(', ') })}
    </span>
  );
}

function AgendaRow({ entry, isToday, onMove, clashes }: { entry: AgendaEntry; isToday: boolean; onMove: (h: HabitRec) => void; clashes?: string[] }) {
  const { t } = useI18n();
  const { ctx } = useApp();
  const moveTask = useMoveTask();

  if (entry.kind === 'date') {
    return (
      <div className="card row" role="group" aria-label={entry.title}>
        <CalendarDays size={20} aria-hidden="true" />
        <span style={{ flex: 1, overflowWrap: 'anywhere' }}>{entry.title}</span>
        <span className="muted xsmall">{t('brain.kinds.date')}</span>
      </div>
    );
  }

  if (entry.kind === 'habit') {
    // Hoy se usa la tarjeta completa, con sus acciones. Otros días, el estado y el detalle.
    if (isToday && entry.item)
      return (
        <div className="stack" style={{ gap: 'var(--space-1)' }}>
          <HabitCard habit={entry.habit!} item={entry.item} streak={null} />
          {(entry.time || clashes) && (
            <div className="row small" style={{ paddingLeft: 'var(--space-2)', flexWrap: 'wrap' }}>
              <TimeRange entry={entry} />
              <Clash names={clashes} />
            </div>
          )}
        </div>
      );
    return (
      <Link to={`/habits/${entry.habit!.id}`} className="card row" style={{ textDecoration: 'none', color: 'inherit' }}>
        <span style={{ flex: 1, overflowWrap: 'anywhere' }}>
          <TimeRange entry={entry} />
          {entry.title} <Clash names={clashes} />
        </span>
        <span className="muted xsmall">{t(`habit.status.${entry.item?.ue?.status ?? 'pending'}`)}</span>
      </Link>
    );
  }

  const h = entry.habit!;
  const tomorrow = addDays(ctx.today, 1);
  const move = (date: LocalDate) => moveTask(h, date, h.dueTime ?? null);
  const draggable = !entry.done && Boolean(h.dueDate || entry.overdue || isToday);
  return (
    <div
      className={`card stack${draggable ? ' agenda__draggable' : ''}`}
      role="group"
      aria-label={entry.title}
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData(DRAG_TYPE, h.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
    >
      <label className="row" style={{ alignItems: 'flex-start' }}>
        <TaskCheckbox task={h} done={entry.done} title={entry.title} />
        <span style={{ flex: 1, overflowWrap: 'anywhere', textDecoration: entry.done ? 'line-through' : undefined }}>
          <TimeRange entry={entry} />
          {entry.title}
          {entry.overdue && <span className="badge badge--overdue" style={{ marginLeft: 6 }}>{t('habit.status.overdue')}</span>} <Clash names={clashes} />
        </span>
        {draggable && <GripVertical size={18} className="agenda__grip" aria-hidden="true" />}
      </label>
      {!entry.done && (
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {(entry.overdue || isToday) && h.dueDate !== tomorrow && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => void move(tomorrow)}>
              {t('agenda.moveTomorrow')}
            </button>
          )}
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => onMove(h)}>
            <CalendarClock size={16} aria-hidden="true" /> {t('agenda.moveOther')}
          </button>
        </div>
      )}
    </div>
  );
}

function RescheduleSheet({ task, today, onClose }: { task: HabitRec | null; today: LocalDate; onClose: () => void }) {
  const { t } = useI18n();
  const move = useMoveTask();
  return (
    <Sheet open={task !== null} onClose={onClose} title={t('agenda.moveTitle')} description={task?.name}>
      {task && (
        <RescheduleForm
          key={task.id}
          task={task}
          today={today}
          onDone={async (date, time) => {
            await move(task, date, time);
            onClose();
          }}
        />
      )}
    </Sheet>
  );
}

function RescheduleForm({ task, today, onDone }: { task: HabitRec; today: LocalDate; onDone: (date: LocalDate, time: string | null) => void }) {
  const { t } = useI18n();
  const [date, setDate] = useState<string>(task.dueDate && task.dueDate >= today ? task.dueDate : today);
  const [time, setTime] = useState<string>(task.dueTime ?? '');
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (date) onDone(date as LocalDate, time || null);
      }}
    >
      <Field label={t('editor.dueDate')}>{(p) => <input {...p} type="date" className="input" required min={today} value={date} onChange={(e) => setDate(e.target.value)} />}</Field>
      <Field label={`${t('editor.dueTime')} (${t('common.optional')})`}>{(p) => <input {...p} type="time" className="input num" value={time} onChange={(e) => setTime(e.target.value)} />}</Field>
      <button type="submit" className="btn btn--primary btn--block">
        {t('agenda.moveConfirm')}
      </button>
    </form>
  );
}

/** Casilla con estado optimista: responde al instante y se alinea con lo guardado al llegar. */
function TaskCheckbox({ task, done, title }: { task: HabitRec; done: boolean; title: string }) {
  const { t } = useI18n();
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  if (optimistic !== null && optimistic === done) setOptimistic(null);
  const checked = optimistic ?? done;
  return (
    <input
      type="checkbox"
      checked={checked}
      aria-label={`${checked ? t('habit.actions.uncomplete') : t('habit.actions.complete')}: ${title}`}
      onChange={(e) => {
        setOptimistic(e.target.checked);
        void setTaskDone(task, e.target.checked).catch(() => setOptimistic(null));
      }}
    />
  );
}
